import { useState } from 'react'
import toast from 'react-hot-toast'
import { useClaimObjective, useObjectives, type ObjectiveDto } from '@/api/hooks'
import { GoldButton } from '@/components/game/ui'
import { PrestigeHint } from '@/components/PrestigeHint'
import {
  TargetIcon, ChevronRight, ChevronDown, CheckIcon,
  GoldIcon, WoodIcon, RumIcon, PrestigeIcon,
} from '@/components/game/GameIcons'

/**
 * Dashboard "what to do next" panel. Shows claimable objectives first, then
 * in-progress (with a bar), and folds finished ones away. Drives direction
 * for the solo game.
 */
/** Show only this many in-progress objectives before folding the rest away. */
const ACTIVE_PREVIEW = 4

export function ObjectivesPanel() {
  const { data, isLoading } = useObjectives()
  const claim = useClaimObjective()
  const [showDone, setShowDone] = useState(false)
  const [showAllActive, setShowAllActive] = useState(false)

  if (isLoading || !data) {
    return (
      <section className="rounded-2xl border border-sand-500/20 bg-gradient-to-b from-ocean-700/30 to-[#06222b]/40 p-6">
        <h2 className="font-display text-2xl text-sand-100 flex items-center gap-2.5">
          <TargetIcon className="w-6 h-6 text-sand-300" /> Objectifs
        </h2>
        <p className="text-sm text-ocean-300 mt-2">Chargement…</p>
      </section>
    )
  }

  const claimable = data.objectives.filter((o) => o.completed && !o.claimed)
  const active = data.objectives.filter((o) => !o.completed)
  const done = data.objectives.filter((o) => o.claimed)
  const visibleActive = showAllActive ? active : active.slice(0, ACTIVE_PREVIEW)

  const handleClaim = (o: ObjectiveDto) => {
    claim.mutate(o.key, {
      onSuccess: () => toast.success(`${o.title} : récompense empochée !`, { duration: 4000 }),
      onError: (e) => toast.error(e.message || 'Réclamation impossible'),
    })
  }

  return (
    <section className="rounded-2xl border border-sand-500/20 bg-gradient-to-b from-ocean-700/30 to-[#06222b]/40 p-6 space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-2xl text-sand-100 flex items-center gap-2.5">
          <TargetIcon className="w-6 h-6 text-sand-300" /> Objectifs
        </h2>
        <span className="text-xs text-ocean-300">{done.length}/{data.objectives.length} accomplis</span>
      </div>

      {claimable.length === 0 && active.length === 0 && (
        <p className="text-sm text-sand-300">Tous les objectifs sont accomplis, capitaine. Légende vivante !</p>
      )}

      {/* Claimable first — the satisfying bit. */}
      {claimable.map((o) => (
        <ObjectiveRow key={o.key} o={o} highlight>
          <GoldButton
            onClick={() => handleClaim(o)}
            disabled={claim.isPending}
            className="text-xs px-3 py-1.5 shrink-0"
          >
            Réclamer
          </GoldButton>
        </ObjectiveRow>
      ))}

      {/* In-progress with bars — folded to a short preview at startup (18 is a lot). */}
      {visibleActive.map((o) => (
        <ObjectiveRow key={o.key} o={o}>
          <div className="text-xs text-ocean-300 tabular-nums shrink-0">{o.current}/{o.target}</div>
        </ObjectiveRow>
      ))}

      {active.length > ACTIVE_PREVIEW && (
        <button
          onClick={() => setShowAllActive((s) => !s)}
          className="text-xs text-ocean-400 hover:text-ocean-200 transition-colors flex items-center gap-1.5"
        >
          {showAllActive ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
          {showAllActive ? 'Réduire les objectifs' : `Voir ${active.length - ACTIVE_PREVIEW} objectifs de plus`}
        </button>
      )}

      {done.length > 0 && (
        <div>
          <button
            onClick={() => setShowDone((s) => !s)}
            className="text-xs text-ocean-400 hover:text-ocean-200 transition-colors flex items-center gap-1.5"
          >
            {showDone ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
            Accomplis ({done.length})
          </button>
          {showDone && (
            <ul className="mt-2 space-y-1">
              {done.map((o) => (
                <li key={o.key} className="text-xs text-ocean-400 flex items-center gap-2">
                  <CheckIcon className="w-3.5 h-3.5 text-green-400" />
                  <span className="line-through">{o.title}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  )
}

function ObjectiveRow({ o, children, highlight }: { o: ObjectiveDto; children: React.ReactNode; highlight?: boolean }) {
  const pct = o.target > 0 ? Math.min(100, (o.current / o.target) * 100) : 0

  return (
    <div className={`rounded-lg p-3 ${highlight ? 'border border-sand-300/40 bg-sand-300/5' : 'border border-sand-500/12 bg-ocean-900/35'}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-sand-200">{o.title}</p>
          <p className="text-xs text-ocean-300">{o.description}</p>
          <p className="text-[11px] text-ocean-400 mt-0.5 flex items-center gap-2 flex-wrap">
            <span>Récompense :</span>
            <Reward o={o} />
          </p>
        </div>
        {children}
      </div>
      {!o.completed && (
        <div className="mt-2 h-1.5 bg-ocean-900 rounded-full overflow-hidden">
          <div className="h-full bg-ocean-300 rounded-full transition-all" style={{ width: `${pct}%` }} />
        </div>
      )}
    </div>
  )
}

function Reward({ o }: { o: ObjectiveDto }) {
  const parts: React.ReactNode[] = []
  if (o.reward.gold) parts.push(<span key="g" className="inline-flex items-center gap-1"><GoldIcon className="w-3.5 h-3.5 text-sand-300" />{o.reward.gold}</span>)
  if (o.reward.wood) parts.push(<span key="w" className="inline-flex items-center gap-1"><WoodIcon className="w-3.5 h-3.5 text-sand-300" />{o.reward.wood}</span>)
  if (o.reward.rhum) parts.push(<span key="r" className="inline-flex items-center gap-1"><RumIcon className="w-3.5 h-3.5 text-sand-300" />{o.reward.rhum}</span>)
  if (o.reward.prestige) parts.push(<span key="p" className="group/tip relative inline-flex items-center gap-1 cursor-help"><PrestigeIcon className="w-3.5 h-3.5 text-sand-300" />{o.reward.prestige} prestige<PrestigeHint /></span>)
  if (parts.length === 0) return <span>—</span>
  return <>{parts.flatMap((p, i) => (i ? [<span key={`s${i}`} className="text-sand-500/60">·</span>, p] : [p]))}</>
}
